import React, { useMemo, useState, useRef, useCallback, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import isEqualWith from 'lodash/isEqualWith';
import Joi from 'joi';

import { useSendCompanyBeneficiariesMutation } from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import {
    fillArrayToMatchSize,
    isStatusOk,
    jsonParseSafe,
    setTunnelChangesHandlers,
} from 'utils/helpers';
import { docTypes } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { TunnelBox, TunnelStep, DocumentUpload } from 'containers/tunnel/common';
import { CommonForm } from './common-components/CommonForm';
import { CommonPPTableRows } from './common-components/CommonRows';
import { TableRowButtons } from './common-components/TableRowButtons';
import PoliticallyExposedForm from '../contact-info/forms/PoliticallyExposedForm';
import { SubmitButton, ErrorMessage } from 'components';
import { Typography, TableCell, TableRow, FormHelperText } from '@mui/material';

const defaultErrors = { lastname: '', firstname: '', politicallyExposedReason: '' };

const validationSchema = {
    lastname: Joi.string().required(),
    firstname: Joi.string().required(),
    address: Joi.string().required(),
    birthDate: Joi.string().required(),
    cityOfBirth: Joi.string().required(),
    nationality: Joi.string().required(),
    documents: Joi.array().min(1).messages({ 'array.min': texts.EMPTY_PARTNER_LIST }),
    politicallyExposedPerson: Joi.string().required(),
};

const propsComparison = (prevProps, nextProps) => {
    return (
        isEqual(prevProps.values, nextProps.values) &&
        isEqual(prevProps.errors, nextProps.errors) &&
        isEqual(prevProps.removeRow, nextProps.removeRow)
    );
};

const IndividualsTableRows = memo(
    ({
        classes,
        values: { documents, ...rest },
        index,
        handleChange,
        handleAddressChange,
        handleDateChange,
        handlePoliticallyExposedReasonChange,
        removeRow,
        handleTableFileChange,
        removeFile,
        errors,
    }) => (
        <TableRow key={index} className={classes.tableRow}>
            <CommonPPTableRows
                values={rest}
                handleChange={handleChange}
                handleAddressChange={handleAddressChange}
                handleDateChange={handleDateChange}
                hasNationality
                errors={errors}
            />
            <TableCell classes={{ root: classes.filesTableCell }}>
                <DocumentUpload
                    selectedFiles={documents}
                    handleFileChange={handleTableFileChange}
                    removeFile={removeFile}
                    id={`table-file-input-${index}`}
                    width={350}
                    isTableCell
                    filesNamesInline
                />
                <div style={{ textAlign: 'start' }}>
                    <PoliticallyExposedForm
                        politicallyExposed={{
                            politicallyExposedPerson: rest.politicallyExposedPerson,
                            politicallyExposedReason: rest.politicallyExposedReason,
                        }}
                        onChange={handleChange}
                        onChangeReason={handlePoliticallyExposedReasonChange}
                        row
                    />
                    {errors?.politicallyExposedReason && (
                        <ErrorMessage message={errors.politicallyExposedReason} />
                    )}
                </div>
            </TableCell>
            <TableRowButtons index={index} removeRow={removeRow} />
        </TableRow>
    ),
    propsComparison
);

const ownerTableHeaders = [
    texts.NOM,
    texts.PRENOMS,
    texts.ADRESSE_DU_DOMICILE_PERSONNEL,
    texts.DATE_DE_NAISSANCE_TABLE_HEADER,
    texts.BIRTHPLACE,
    texts.NATIONALITY,
    texts.ID_UPLOAD,
];

const ownersPP = {
    lastname: '',
    firstname: '',
    address: '',
    birthDate: null,
    cityOfBirth: '',
    nationality: 'FR',
    nif: '',
    politicallyExposedPerson: 'false',
    politicallyExposedReason: { 1: false, 2: false, 3: false },
    documents: [],
};

const TableTitlePP = () => (
    <Typography variant="subtitle2" gutterBottom>
        {texts.VIGILANCE_OWNER_TABLE_PP}
    </Typography>
);

const VigilanceOwner = props => {
    const { handleNextStep, userStep } = props;

    const tableRefPP = useRef();
    const [sendCompanyBeneficiaries, { isLoading: buttonLoader, isSuccess }] =
        useSendCompanyBeneficiariesMutation();

    const internalUser = useSelector(userSelectors.getInternalUser);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);

    const isResubmit = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);
    const [hasEmptyFields, setHasEmptyFields] = useState(false);
    const subscriptionIsTransfered =
        (tunnelSubscription.createdBy && tunnelSubscription.transferredToInvestorAt) ||
        !tunnelSubscription.createdBy;

    const initialValues = useMemo(
        () => ({
            beneficiaryListedCompany: !!internalUser?.investorCompany?.beneficiaryListedCompany,
            beneficiaryPublicAuthority: !!internalUser?.investorCompany?.beneficiaryPublicAuthority,
        }),
        [internalUser]
    );

    const initialFiles = useMemo(
        () => internalUser?.investorCompany?.beneficiariesReducedVigilanceDocuments || [],
        [internalUser]
    );

    const initialIdentifyFiles = useMemo(
        () => internalUser?.investorCompany?.beneficiaryIdentifyDocuments || [],
        [internalUser]
    );

    const initialBeneficiariesPP = useMemo(() => {
        if (internalUser?.investorCompany) {
            return internalUser?.investorCompany.beneficiariesPP?.map(benef => {
                const { politicallyExposedPersonReason, id, ...beneficiary } = benef;
                const reason = jsonParseSafe(politicallyExposedPersonReason) || {};
                return {
                    ...beneficiary,
                    politicallyExposedPerson:
                        beneficiary?.politicallyExposedPerson === true ? 'true' : 'false',
                    politicallyExposedReason: {
                        1: !!reason.choice?.includes('step5.question_15.option_1'),
                        2: !!reason.choice?.includes('step5.question_15.option_2'),
                        3: !!reason.choice?.includes('step5.question_15.option_3'),
                    },
                };
            });
        }
        return [];
    }, [internalUser]);

    const [reducedVigilance, setReducedVigilance] = useState(initialValues);
    const [selectedFiles, setSelectedFiles] = useState(initialFiles);
    const [selectedListFiles, setSelectedListFiles] = useState(initialIdentifyFiles);
    const [displayUpload, setDisplayUpload] = useState(false);
    const [displayTable, setDisplayTable] = useState(false);
    const [beneficiariesPP, setBeneficiariesPP] = useState(
        tableRefPP.current?.getFormState() || []
    );

    useEffect(() => {
        setReducedVigilance(initialValues);
    }, [initialValues]);

    const handleFileChange =
        setFilesState =>
        ({ target: { files } }) => {
            const file = files[0];
            tableRefPP.current?.setFormErrors([]);
            setFilesState([file]);
        };

    const removeFile = useCallback(
        (filesState, filesSetter) =>
            ({ currentTarget: { id } }) => {
                const documentFound = filesState.find((file, i) => i === +id && file.id);
                if (documentFound) {
                    const docId = documentFound.id;
                    filesSetter(prevState => {
                        return prevState.filter(file => file.id !== docId || !file.id);
                    });
                } else {
                    filesSetter(filesState.filter((_, i) => i !== +id));
                }
            },
        []
    );

    useEffect(() => {
        const displayUpload = Object.keys(reducedVigilance).some(
            value => reducedVigilance[value] === true
        );

        const displayTable = Object.keys(reducedVigilance).every(
            value => reducedVigilance[value] === false
        );

        if (!displayUpload && selectedFiles?.length) {
            setSelectedFiles([]);
        }

        if (!displayTable) {
            //clear table rows
            tableRefPP.current?.clearTableState();
        }

        setDisplayUpload(displayUpload);
        setDisplayTable(displayTable);
    }, [reducedVigilance, selectedFiles]);

    useEffect(() => {
        if (isResubmit && isSuccess) {
            const files =
                internalUser?.investorCompany?.beneficiariesReducedVigilanceDocuments || [];
            setSelectedFiles(files);
        }
    }, [internalUser, setSelectedFiles, isResubmit, isSuccess]);

    useEffect(() => {
        if (isSuccess) {
            handleNextStep(true);
        }
    }, [handleNextStep, isSuccess]);

    const submitPayload = useCallback(
        (payload, callback) => {
            sendCompanyBeneficiaries({ payload, callback });
        },
        [sendCompanyBeneficiaries]
    );

    const handleSubmit = useCallback(
        afterSubmitCallback => () => {
            let tableFilesformData = new FormData();

            const reducedVigilienceFilesToRemove = initialFiles
                .filter(
                    document => !selectedFiles.find(selectedFile => selectedFile.id === document.id)
                )
                .map(document => document.id);

            const identifyFilesToRemove = initialIdentifyFiles
                .filter(
                    document =>
                        !selectedListFiles.find(selectedFile => selectedFile.id === document.id)
                )
                .map(document => document.id);

            const allInitialBeneficiariesDocuments = initialBeneficiariesPP.flatMap(
                beneficiary => beneficiary.documents || []
            );
            const allBeneficiariesDocuments = beneficiariesPP.flatMap(
                beneficiary => beneficiary.documents || []
            );

            const beneficiariesFilesToRemove = allInitialBeneficiariesDocuments
                .filter(
                    document =>
                        !allBeneficiariesDocuments.find(
                            selectedFile => selectedFile.id === document.id
                        )
                )
                .map(document => document.id);

            const filesToRemove = reducedVigilienceFilesToRemove.concat(
                identifyFilesToRemove,
                beneficiariesFilesToRemove
            );

            beneficiariesPP.forEach(state => {
                state.documents.forEach(document => {
                    if (!document.id) {
                        return tableFilesformData.append('files[]', document);
                    }
                });
            });
            tableFilesformData.append('type', docTypes.BENEFICIARY_DOCUMENT);

            const validationDataPP = tableRefPP.current?.validateAllForms();
            if (!validationDataPP.every(form => !!form.valid || form.isEmptyLine)) {
                setHasEmptyFields(true);
            }
            const ppTableEmpty = tableRefPP.current?.isEmpty();
            let ppTableValid =
                !ppTableEmpty &&
                validationDataPP.every(form => !!form.valid || form.isEmptyLine) &&
                selectedListFiles.length > 0 &&
                displayTable;

            if (selectedListFiles.length === 0) {
                ppTableValid = false;
                if (subscriptionIsTransfered) {
                    setIsFileMissing(true);
                }
            }

            if (ppTableValid || !subscriptionIsTransfered) {
                // remove the error status if either table is valid
                tableRefPP.current?.setFormErrors([]);

                const payload = {
                    ownerForm: {
                        reducedVigilance,
                        selectedFiles,
                        selectedListFiles,
                        beneficiariesPP,
                    },
                    filesToRemove,
                    documents: tableFilesformData ?? undefined,
                };
                submitPayload(payload, afterSubmitCallback);
            } else {
                tableRefPP.current?.setFormErrors(validationDataPP);
            }
        },
        [
            displayTable,
            submitPayload,
            reducedVigilance,
            selectedFiles,
            selectedListFiles,
            beneficiariesPP,
            initialFiles,
            initialIdentifyFiles,
            initialBeneficiariesPP,
            subscriptionIsTransfered,
            setHasEmptyFields,
        ]
    );

    const checkChanges = useCallback(() => {
        return (
            !isEqual(initialValues, reducedVigilance) ||
            !isEqualWith(initialFiles, selectedFiles, (objValue, othValue) => {
                return (
                    objValue.length === othValue.length &&
                    objValue.every((formObject, i) => isEqual(formObject, othValue[i]))
                );
            }) ||
            !isEqualWith(initialIdentifyFiles, selectedListFiles, (objValue, othValue) => {
                return (
                    objValue.length === othValue.length &&
                    objValue.every((formObject, i) => isEqual(formObject, othValue[i]))
                );
            }) ||
            !isEqualWith(initialBeneficiariesPP, beneficiariesPP, (objValue, othValue) => {
                let baseBeneficiaries = fillArrayToMatchSize(objValue, othValue, ownersPP);
                return baseBeneficiaries.every((formObject, i) => isEqual(formObject, othValue[i]));
            })
        );
    }, [
        initialValues,
        initialFiles,
        reducedVigilance,
        selectedFiles,
        initialIdentifyFiles,
        selectedListFiles,
        initialBeneficiariesPP,
        beneficiariesPP,
    ]);

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);
    const [isFileMissing, setIsFileMissing] = useState(false);

    useEffect(
        () => setTunnelChangesHandlers(handleSubmit, checkChanges, isResubmit),
        [handleSubmit, checkChanges, isResubmit]
    );

    const handleTableChangePP = useCallback(() => {
        const beneficiariesState = tableRefPP.current?.getFormState() || [];
        setBeneficiariesPP(beneficiariesState);
    }, []);

    return (
        <TunnelStep title={texts.VIGILANCE_OWNER} tunnelStepWidth={1350} maxWidth={1350}>
            <TunnelBox>
                {displayUpload && (
                    <DocumentUpload
                        label={texts.STATUS_UPLOAD_FILE}
                        selectedFiles={selectedFiles}
                        handleFileChange={handleFileChange(setSelectedFiles)}
                        removeFile={removeFile(selectedFiles, setSelectedFiles)}
                    />
                )}
                <CommonForm
                    usesPoliticallyExposedData
                    TableTitle={TableTitlePP}
                    TableRows={IndividualsTableRows}
                    tableHeaders={ownerTableHeaders}
                    tableDefaultValues={ownersPP}
                    tableValuesProps={initialBeneficiariesPP}
                    validationSchema={
                        tunnelSubscription.indirectUntransferred ? {} : validationSchema
                    }
                    defaultFormErrors={defaultErrors}
                    isResubmit={isResubmit}
                    ref={tableRefPP}
                    formSubmitted={isSuccess}
                    displayTable={displayTable}
                    onChange={handleTableChangePP}
                />
                <Typography variant="body1">{texts.LISTE_DES_BENEFICIAIRES_EFFECTIFS}</Typography>
                <DocumentUpload
                    id="list"
                    selectedFiles={selectedListFiles}
                    handleFileChange={handleFileChange(setSelectedListFiles)}
                    removeFile={removeFile(selectedListFiles, setSelectedListFiles)}
                    error={isFileMissing ? texts.FILE_MISSING : ''}
                />
                {hasEmptyFields && (
                    <FormHelperText error>{texts.VIGILANCE_OWNER_EMPTY_FIELDS}</FormHelperText>
                )}
            </TunnelBox>
            <SubmitButton
                text={isResubmit && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT}
                onClick={changesMade || !hasStatusOk ? handleSubmit() : handleNextStep}
                loading={buttonLoader}
                disabled={buttonLoader}
            />
        </TunnelStep>
    );
};

VigilanceOwner.propTypes = {
    handleNextStep: PropTypes.func,
    userStep: PropTypes.object,
};

export default VigilanceOwner;
